import React, { useCallback, useEffect, useMemo, useState } from "react";
import { DialogContent, Button } from "@mui/material";
import {
  Box,
  Dialog,
  DialogTitle,
  Drawer,
  IconButton,
  Typography,
} from "@mui/material";
import { CheckCircleOutlineOutlined, CloseOutlined } from "@mui/icons-material";
import DynamicForm from "../../../common/DynamicForm/DynamicForm";
import { useAppDispatch, useAppSelector } from "../../../store/hooks";
import { RootState } from "../../../store/store";
import { IAsyncOptionFn, IGetOptionsResponse, IDynamicFormFieldsJson } from "../../../models/user/DynamicForm/DynamicForm";
import { CustomAction } from "../../../store/customAction";
import { API_GET_LOOKUP_VALUES } from "../../../Apis/ApiEndPoints";
import { PostAddDriver } from "../../../store/user/policyChange/policyChangeActions";
import { IDriverPolicyChangeRequestParams } from "../../../models/user/PolicyChange/PolicyChange";
import { GetAddDriverFormJson } from "../../../store/user/driver/driverActions";
import MessageDialog from "../../../components/MessageDialog/MessageDialog";
import { setCalledReducerType } from "../../../store/common/CommonSlice";
import { Api } from "../../..";
import { useTranslation } from "react-i18next";
import { IPolicyDriver } from "../../../models/user/policyDetails/PolicyDetails";
import dayjs from "dayjs";

export interface EditDriverPanelProps {
  policyId: string;
  carrier: string;
  lobCode: string;
  system: string;
  policyNumber?: string;
  driver: Partial<IPolicyDriver> | null;
  onAction?: (action: { followup_message?: string; label?: string }) => void;
}

const EditDriverPanel: React.FC<EditDriverPanelProps> = ({ policyId, carrier, lobCode, system, policyNumber, driver, onAction }) => {
  const dispatch = useAppDispatch();
  const { t } = useTranslation();

  const { userDetails } = useAppSelector((state: RootState) => state.userAuth);
  const { policyDetailsHeader } = useAppSelector((state: RootState) => state.policyDetails);
  const { addDriverFormFieldsJson } = useAppSelector((state: RootState) => state.driver);
  const { isSuccess, isError, calledReducerType } = useAppSelector((state: RootState) => state.common);

  const [messageDialogOpen, setMessageDialogOpen] = useState<boolean>(false);
  const [formFieldsJson, setFormFieldsJson] = useState<IDynamicFormFieldsJson | null>(null);

  const asyncFunctions: { [key: string]: IAsyncOptionFn } = useMemo(
    () => ({
      getPrefixOptions: async (fieldName) => {
        try {
          const response: IGetOptionsResponse = await CustomAction(Api.get(`${API_GET_LOOKUP_VALUES}/${fieldName}`), dispatch, "getPrefixOptions");
          return response.data;
        } catch (error) {
          return [];
        }
      },
      getMaritalStatusOptions: async (fieldName) => {
        try {
          const response: IGetOptionsResponse = await CustomAction(Api.get(`${API_GET_LOOKUP_VALUES}/${fieldName}`), dispatch, "getMaritalStatusOptions");
          return response.data;
        } catch (error) {
          return [];
        }
      },
      getGenderOptions: async (fieldName) => {
        try {
          const response: IGetOptionsResponse = await CustomAction(Api.get(`${API_GET_LOOKUP_VALUES}/${fieldName}`), dispatch, "getGenderOptions");
          return response.data;
        } catch (error) {
          return [];
        }
      },
      getRelationshipToInsuredOptions: async (fieldName) => {
        try {
          const response: IGetOptionsResponse = await CustomAction(Api.get(`${API_GET_LOOKUP_VALUES}/${fieldName}`), dispatch, "getRelationshipToInsuredOptions");
          return response.data;
        } catch (error) {
          return [];
        }
      },
      getEmploymentStatusOptions: async (fieldName) => {
        try {
          const response: IGetOptionsResponse = await CustomAction(Api.get(`${API_GET_LOOKUP_VALUES}/${fieldName}`), dispatch, "getEmployementStatusOptions");
          return response.data;
        } catch (error) {
          return [];
        }
      },
      getEducationLevelOptions: async (fieldName) => {
        try {
          const response: IGetOptionsResponse = await CustomAction(Api.get(`${API_GET_LOOKUP_VALUES}/${fieldName}`), dispatch, "getEducationLevelOptions");
          return response.data;
        } catch (error) {
          return [];
        }
      },
      getLicensingStateOptions: async (fieldName) => {
        try {
          const response: IGetOptionsResponse = await CustomAction(Api.get(`${API_GET_LOOKUP_VALUES}/${fieldName}`), dispatch, "getLicensingStateOptions");
          return response.data;
        } catch (error) {
          return [];
        }
      },
      getDriverLicenseStatusOptions: async (fieldName) => {
        try {
          const response: IGetOptionsResponse = await CustomAction(Api.get(`${API_GET_LOOKUP_VALUES}/${fieldName}`), dispatch, "getDriverLicenseStatusOptions");
          return response.data;
        } catch (error) {
          return [];
        }
      },
    }),
    [dispatch]
  );

  const handleEditDriverFromOnSubmit = () => {
    setMessageDialogOpen(true);
        const pn = policyNumber ?? policyDetailsHeader?.carrierPolicyNumber;
        if (pn && onAction) {
          onAction({ followup_message: `Show me details for policy ${pn}` });
        }
  }
  
  const handleMessageDialogClose = useCallback(() => {
    setMessageDialogOpen(false);
    dispatch(setCalledReducerType(null));
  }, [dispatch]);

  const handleCancel = useCallback(() => {
    const pn = policyNumber ?? policyDetailsHeader?.carrierPolicyNumber;
    if (pn && onAction) {
      onAction({ followup_message: `Show me details for policy ${pn}` });
    }
  }, [onAction, policyNumber, policyDetailsHeader?.carrierPolicyNumber]);

  useEffect(() => {
    dispatch(GetAddDriverFormJson());
  }, [dispatch]);

  useEffect(() => {
    if (addDriverFormFieldsJson && driver) {
      const updatedFormFieldsJson = {
        ...addDriverFormFieldsJson,
        sections:
          addDriverFormFieldsJson?.sections?.map((section) => ({
            ...section,
            fields: section?.fields?.map((field) => {
              const updatedField = { ...field };

              if (updatedField.name === "givenName") {
                updatedField.value = driver?.givenName || "";
              } else if (updatedField.name === "otherGivenName") {
                updatedField.value = driver?.otherGivenName || "";
              } else if (updatedField.name === "surName") {
                updatedField.value = driver?.surName || "";
              } else if (updatedField.name === "maritalStatus") {
                updatedField.value = driver?.maritalStatus?.toLowerCase() || "";
              } else if (updatedField.name === "genderCd") {
                updatedField.value = driver?.genderCd?.toLowerCase() || "";
              } else if (updatedField.name === "relationshipToInsuredCd") {
                updatedField.value = driver?.relationshipToInsuredCd?.toLowerCase() || "";
              } else if (updatedField.name === "licenseNumber") {
                updatedField.value = driver?.licenseNumber || "";
              } else if (updatedField.name === "licensedStateProvCd") {
                updatedField.value = driver?.licensedStateProvCd || "";
              } else if (updatedField.name === "dateOfBirth") {
                updatedField.value = driver?.dateOfBirth ? dayjs(driver.dateOfBirth) : null;
              } else if (updatedField.name === "licenseDt") {
                updatedField.value = driver?.licenseDt ? dayjs(driver.licenseDt) : null;
              }
              return updatedField;
            }),
          })) || [],
      };
      setFormFieldsJson(updatedFormFieldsJson);
    } else if (addDriverFormFieldsJson) {
      setFormFieldsJson(addDriverFormFieldsJson);
    }
  }, [addDriverFormFieldsJson, driver]);

  useEffect(() => {
    if ((isSuccess || isError) && calledReducerType === "policyChange/PostAddDriver") {
      setMessageDialogOpen(true);
      if (isSuccess) {
        const pn = policyNumber ?? policyDetailsHeader?.carrierPolicyNumber;
        if (pn && onAction) {
          onAction({ followup_message: `Show me details for policy ${pn}` });
        }
      }
    }
  }, [isSuccess, isError, calledReducerType, policyNumber, policyDetailsHeader, onAction]);

  return (
    <>
      <DialogContent>
        {formFieldsJson ? (
          <DynamicForm
            formFieldsJson={formFieldsJson}
            asyncOptionsFunctions={asyncFunctions}
            onFormSubmit={handleEditDriverFromOnSubmit}
            rightActions={
              <Button variant="contained" color="primary" onClick={handleCancel}>
                {t("Cancel")}
              </Button>
            }
          />
        ) : (
          <></>
        )}
      </DialogContent>
      <Dialog open={messageDialogOpen} sx={{ backdropFilter: "blur(5px)" }}>
              <DialogTitle
                sx={{
                  display: "flex",
                  justifyContent: "flex-end",
                  alignItems: "center",
                }}
              >
                <IconButton onClick={handleMessageDialogClose}>
                  <CloseOutlined />
                </IconButton>
              </DialogTitle>
      
              <DialogContent>
                <Box
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    flexDirection: "column",
                    justifyContent: "center",
                    gap: 1,
                    width: "100%",
                  }}
                >
                  <CheckCircleOutlineOutlined
                    sx={{ fontSize: "60px", color: "green" }}
                  />
                  <Typography>{t("Policy Change has been initiated")}</Typography>
                </Box>
              </DialogContent>
            </Dialog>
      {/* <MessageDialog open={messageDialogOpen} handleClose={handleMessageDialogClose} /> */}
    </>
  );
};

export default EditDriverPanel;

